import type { Mixpanel } from 'mixpanel-browser';
import MixpanelLibrary from 'mixpanel-browser';

export type SpotOnApp = 'electra-spot-on';

/**
 * Initialize Mixpanel with Session Replay enabled
 *
 * Session Replay Configuration:
 * - Free Plan: 10,000 monthly replays
 * - Growth Plan: 20,000 monthly replays
 * - Enterprise: Custom add-ons available
 *
 * Privacy Controls:
 * - Add class="mp-block" to elements you want to completely hide from recordings
 * - Add class="mp-mask" to elements you want to mask (e.g., sensitive text)
 * - By default, all password fields and inputs with type="password" are automatically masked
 *
 * Example:
 * <input type="text" className="mp-mask" /> // Text will be masked as ***
 * <div className="mp-block">Sensitive content</div> // Won't appear in replay
 *
 * @param token - Mixpanel project token
 * @param app - Application name
 * @param debug - Enable debug mode for development
 */
export const initMixpanel = (token: string, app: SpotOnApp, debug: boolean): Mixpanel | null => {
  if (!token) {
    console.warn('MixPanel token not provided - analytics tracking disabled');
    return null;
  }
  return MixpanelLibrary.init(
    token,
    {
      api_host: 'https://api-eu.mixpanel.com',
      debug,
      track_pageview: false,
      // Enable Session Replay
      record_sessions_percent: 100, // Record 100% of sessions (adjust based on your needs)
      record_block_class: 'mp-block', // CSS class to block elements from recording
      record_mask_text_class: 'mp-mask', // CSS class to mask text in recordings
      record_collect_fonts: true, // Collect fonts for better replay quality
      record_heatmap_data: true,
      record_canvas: true, // Enable canvas recording for Mapbox maps
    },
    app,
  );
};

/** EVENTS */
export const BUTTON_CLICKED = {
  CREATE_PROJECT: 'create_project',
  UPLOAD_CSV: 'upload_csv',
  MAPBOX_SUGGESTION_SELECTED: 'mapbox_suggestion_selected',
  MAP_INTERACTION: 'map_interaction',
  SIGN_OUT: 'sign_out',
  SIGN_IN: 'sign_in',
  DOWNLOAD_CSV: 'download_csv',
  DRIVING_DISTANCE: 'driving_distance',
  FORECAST_SECTION: 'forecast_section',
} as const;

export const PAGE_VIEWED = {
  DASHBOARD_PAGE: 'dashboard',
  PROJECT_PAGE: 'project', // params: project_id
} as const;

export const ACTION_TRACKED = {
  PROJECT_CREATED: 'project_created',
  PROJECT_CANCELLED: 'project_cancelled',
  CSV_UPLOAD: 'csv_upload', // CSV upload step (before project creation)
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  ITINERARY_CREATED: 'itinerary_created',
  FEEDBACK_CAPTURED_AADT: 'feedback_captured_aadt',
  FEEDBACK_FORECAST: 'feedback_forecast',
  // Map location inspection: emitted when a workspace-location detail popup
  // opens on /map. Project-agnostic — the payload MUST NOT carry a project_id.
  LOCATION_INSPECTED: 'location_inspected',
  // Map manual-pin add: emitted on a successful save of one or more
  // manually-added locations. Project-agnostic; carries the batch `count` only.
  LOCATIONS_ADDED: 'locations_added',
  // Map manual-pin remove: emitted when a saved workspace location is removed.
  LOCATION_REMOVED: 'location_removed',
  WORKSPACE_CREATED: 'workspace_created',
  WORKSPACE_UPDATED: 'workspace_updated',
  WORKSPACE_DELETED: 'workspace_deleted',
  // Map radius-recompute: emitted when committing a new radius on an
  // existing workspace location successfully triggers a recompute.
  LOCATION_RADIUS_CHANGED: 'location_radius_changed',
  // Compute-preferences defaults (radius + metrics) saved from the modal.
  COMPUTE_PREFERENCES_SAVED: 'compute_preferences_saved',
  MAP_LAYER_TOGGLED: 'map_layer_toggled',
  // One event per owner ticked or unticked in the map's owner filter, carrying the owner and how
  // many are selected afterwards.
  MAP_OWNER_FILTERED: 'map_owner_filtered',
} as const;

/** TYPES */
export type ButtonName = (typeof BUTTON_CLICKED)[keyof typeof BUTTON_CLICKED];
export type PageName = (typeof PAGE_VIEWED)[keyof typeof PAGE_VIEWED];
export type ActionName = (typeof ACTION_TRACKED)[keyof typeof ACTION_TRACKED];
